import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, User, FileText, Phone, Linkedin } from 'lucide-react';
import '../styles/modal.css';

const EmailGate = ({ onClose, onSubmit, resourceName, gateFields }) => {
  // Initialize form data based on enabled fields
  const getInitialFormData = () => {
    const data = {};
    if (gateFields?.name?.enabled) data.name = '';
    if (gateFields?.email?.enabled) data.email = '';
    if (gateFields?.phone?.enabled) data.phone = '';
    if (gateFields?.linkedin?.enabled) data.linkedin = '';
    return data;
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);

  useEffect(() => {
    // Focus on first input
    firstInputRef.current?.focus();

    // ESC key handler
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate name if enabled and required
    if (gateFields?.name?.enabled && gateFields?.name?.required) {
      if (!formData.name?.trim()) {
        newErrors.name = 'Name is required';
      }
    }
    
    // Validate email if enabled and required
    if (gateFields?.email?.enabled && gateFields?.email?.required) {
      if (!formData.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
    } else if (gateFields?.email?.enabled && formData.email?.trim()) {
      // Validate format even if not required but provided
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
    }
    
    // Validate phone if enabled and required
    if (gateFields?.phone?.enabled && gateFields?.phone?.required) {
      if (!formData.phone?.trim()) {
        newErrors.phone = 'Phone number is required';
      }
    }
    
    // Validate LinkedIn if enabled and required
    if (gateFields?.linkedin?.enabled && gateFields?.linkedin?.required) {
      if (!formData.linkedin?.trim()) {
        newErrors.linkedin = 'LinkedIn profile is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      setErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Determine which field should be first for focus
  const isFirstField = (fieldName) => {
    if (fieldName === 'name' && gateFields?.name?.enabled) return true;
    if (fieldName === 'email' && gateFields?.email?.enabled && !gateFields?.name?.enabled) return true;
    if (fieldName === 'phone' && gateFields?.phone?.enabled && !gateFields?.name?.enabled && !gateFields?.email?.enabled) return true;
    if (fieldName === 'linkedin' && gateFields?.linkedin?.enabled && !gateFields?.name?.enabled && !gateFields?.email?.enabled && !gateFields?.phone?.enabled) return true;
    return false;
  };

  return (
    <div
      ref={modalRef}
      className="modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="gate-title"
    >
      <div className="modal-content email-gate-modal">
        <button
          className="modal-close"
          onClick={onClose}
          aria-label="Close modal"
          disabled={submitting}
        >
          <X size={24} />
        </button>

        <div className="gate-icon">
          <FileText size={48} />
        </div>

        <h2 id="gate-title" className="modal-title">
          Access {resourceName}
        </h2>

        <p className="gate-description">
          Please provide your information to access this resource.
        </p>

        <form onSubmit={handleSubmit} className="gate-form">
          {/* Name Field */}
          {gateFields?.name?.enabled && (
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                <User size={18} />
                <span>Full Name {gateFields?.name?.required && <span className="required-asterisk">*</span>}</span>
              </label>
              <input
                ref={isFirstField('name') ? firstInputRef : null}
                type="text"
                id="name"
                name="name"
                value={formData.name || ''}
                onChange={handleChange}
                className={`form-input ${errors.name ? 'error' : ''}`}
                placeholder="John Doe"
                disabled={submitting}
                required={gateFields?.name?.required}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>
          )}

          {/* Email Field */}
          {gateFields?.email?.enabled && (
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                <Mail size={18} />
                <span>Email Address {gateFields?.email?.required && <span className="required-asterisk">*</span>}</span>
              </label>
              <input
                ref={isFirstField('email') ? firstInputRef : null}
                type="email"
                id="email"
                name="email"
                value={formData.email || ''}
                onChange={handleChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="john@example.com"
                disabled={submitting}
                required={gateFields?.email?.required}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
          )}

          {/* Phone Field */}
          {gateFields?.phone?.enabled && (
            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                <Phone size={18} />
                <span>Phone Number {gateFields?.phone?.required && <span className="required-asterisk">*</span>}</span>
              </label>
              <input
                ref={isFirstField('phone') ? firstInputRef : null}
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                className={`form-input ${errors.phone ? 'error' : ''}`}
                placeholder="+1 (555) 123-4567"
                disabled={submitting}
                required={gateFields?.phone?.required}
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          )}

          {/* LinkedIn Field */}
          {gateFields?.linkedin?.enabled && (
            <div className="form-group">
              <label htmlFor="linkedin" className="form-label">
                <Linkedin size={18} />
                <span>LinkedIn Profile {gateFields?.linkedin?.required && <span className="required-asterisk">*</span>}</span>
              </label>
              <input
                ref={isFirstField('linkedin') ? firstInputRef : null}
                type="url"
                id="linkedin"
                name="linkedin"
                value={formData.linkedin || ''}
                onChange={handleChange}
                className={`form-input ${errors.linkedin ? 'error' : ''}`}
                placeholder="https://linkedin.com/in/johndoe"
                disabled={submitting}
                required={gateFields?.linkedin?.required}
              />
              {errors.linkedin && <span className="error-message">{errors.linkedin}</span>}
            </div>
          )}

          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}

          <button
            type="submit"
            className="gate-submit-btn"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Access Resource'}
          </button>
        </form>

        <p className="gate-privacy">
          Your information is secure and will only be used for energy-related updates.
        </p>
      </div>
    </div>
  );
};

export default EmailGate;
